import React, { useCallback, useState } from 'react'
import { Alert, Col, Form, Row } from 'reactstrap'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import { defaultDateTimeFormat, formatDate } from '@/utils/dateUtilities'
import LoadingIndicator from '@/components/LoadingIndicator'
import Resx from '@/components/Resx'
import { WhiteBox } from '@/components/Layout'
import StatusEnum from '@/components/StatusEnum'
import Icon from '@/components/Icon'
import { defaultChangeHandler, FileAttachments, operations } from '@/components/fileAttachments'
import Name from '@/components/Name'
import context from '@/context'
import { DataLoader } from '@/components/dataLoader'
import { Link } from 'react-router-dom'
import { TaskActivationButton } from '@/internal-app/components/tasks/taskActivationButton'
import { Button } from '@/components/button'
import { RelatedTask } from './relatedTask'
import { ChangeRequestDetails } from '@/internal-app/pages/tasks/changeRequest'
import { TaskResultViewer } from '@/internal-app/pages/tasks/taskResultViewer'
import { workflowTaskStatus } from '@/enums/workflowTaskStatus'
import { DueDateWrapper } from '@/components/dueDateWrapper'
import moment from 'moment'
import { ReassignTaskButton } from '@/internal-app/pages/tasks/reassignTaskButton'
import { getStore } from '@/store'
import { TicketInfo } from '@/internal-app/pages/tickets/ticketInfo'
import { TicketRestartedWarning } from '@/internal-app/pages/tickets/ticketRestartedWarning'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { useLocalization } from '@/hooks/localization'
import PropTypes from 'prop-types'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators as myActiveTaskActionCreators } from '@/store/myActiveTask'
import { actionCreators } from '@/store/myTasks'
import { useSelector } from 'react-redux'

const formatDateTime = (date) => formatDate(date, defaultDateTimeFormat) || '-'

const TaskDetailForm = function TaskDetailForm (props) {
  const {
    task,
    onEditDescriptionButtonClick
  } = props

  const [updateAttachments, activateTask, deactivateTask, getMyTask] = useDispatch([actionCreators.updateAttachments, myActiveTaskActionCreators.activateTask, myActiveTaskActionCreators.deactivateTask, actionCreators.getMyTask])
  const { activeTaskId, activating, descriptionUpdating } = useSelector(state => state.myActiveTask)

  const [attachments, setAttachments] = useState((task || {}).attachments || [])
  const [ticketLoader, setTicketLoader] = useState(() => {
  })
  const [changeItemDeleting, setChangeItemDeleting] = useState(false)

  const isGranted = usePermissionChecker()
  const { localize, rtl } = useLocalization()

  const deleteChangeItem = useCallback((id) => {
    setChangeItemDeleting(true)
    // eslint-disable-next-line promise/catch-or-return
    context.api.changeRequestService.deleteChangeItem(id)
      .catch(err => console.error(err))
      .finally(() => {
        setChangeItemDeleting(false)
        getMyTask(task.id)
      })
  }, [getMyTask, task.id])
  const handleUpdateAttachments = useCallback(() => updateAttachments(task.id, attachments), [attachments, task, updateAttachments])
  const onAttachmentChange = useCallback((e) => setAttachments(defaultChangeHandler(e, attachments)) && (e.operation === operations.remove) && handleUpdateAttachments(), [attachments, handleUpdateAttachments])

  if (!task) {
    return (
      <Alert color='warning'>
        <Resx name='Tasks.CannotFindGivenTask' />
      </Alert>
    )
  }
  const scope = task.type.permissions.filter(x => x.startsWith('Scopes.'))
    .map(x => x.substr(7, x.lastIndexOf('.') - 7).toLowerCase())[0]
  const isActive = task.id === activeTaskId
  const isCurrUserAssignee = parseInt(getStore().getState().identity.user.profile.id) === task.assignedToUserId
  const canEdit = task.status < workflowTaskStatus.enum.Closed
  const canViewCHR = isGranted('CanViewChangeRequests') || isCurrUserAssignee
  // Middle column is active only when related task or result type is present
  const middleColActive = !!(task.relatedTaskId || (canViewCHR && (task.data.resultType || task.changeRequestId)))
  const colSize = middleColActive ? '4' : '6'

  const taskAssignedToId = task && task.assignedToUserId
  // the .sub here is not to create a <sub> element as IDEs might suggest, it's a property from UserInfo() on BE
  const loggedUserId = getStore().getState().identity.user.profile.sub
  const showButton = Number(taskAssignedToId) === Number(loggedUserId)

  const renderTicketDetail = () => {
    return (
      <WhiteBox
        header='Tasks.RelatedTicket'
        buttons={
          <Button color='success' size='sm' icon='revision' onClick={() => ticketLoader.loadData()} />
        }>
        <InfoRow>
          <InfoLabel size='sm'><Resx name='Common.Id' /></InfoLabel>
          <InfoContent><Link to={`/tickets/${task.ticketId}`}><span className='ticket-label'><Icon
            name='list-alt' /> {task.ticketId}</span></Link></InfoContent>
        </InfoRow>
        <DataLoader
          ref={setTicketLoader}
          dataSource={() => context.api.ticketService.get(task.ticketId)}
          render={({ data: ticket, loading }) => (
            loading
              ? <LoadingIndicator />
              : <TicketInfo ticket={ticket} related />
          )} />
      </WhiteBox>
    )
  }

  const renderDescription = () => {
    if (descriptionUpdating) {
      return <LoadingIndicator />
    }
    return (<div className='cms__text'>{rtl ? task.description : task.latinDescription}</div>)
  }

  const renderDescriptionBox = () => {
    const titleAr = `${localize('Common.Description')} ${localize('Common.Arabic')}`
    const onClickAr = (e) => {
      if (!descriptionUpdating) onEditDescriptionButtonClick('ar', titleAr)
      else e.preventDefault()
    }

    const titleEn = `${localize('Common.Description')} ${localize('Common.English')}`
    const onClickEn = (e) => {
      if (!descriptionUpdating) onEditDescriptionButtonClick('en', titleEn)
      else e.preventDefault()
    }

    return (
      <React.Fragment>
        <InfoRow>
          <InfoLabel size='sm'>{titleAr}</InfoLabel>
          <InfoContent>
            {renderDescription()}
            {canEdit ? <a onClick={onClickAr}><Icon name='pencil' /> <span>{localize('Common.Edit')}</span></a> : null}
          </InfoContent>
        </InfoRow>
        <InfoRow>
          <InfoLabel size='sm'>{titleEn}</InfoLabel>
          <InfoContent>
            {renderDescription()}
            {canEdit ? <a onClick={onClickEn}><Icon name='pencil' /> <span>{localize('Common.Edit')}</span></a> : null}
          </InfoContent>
        </InfoRow>
      </React.Fragment>
    )
  }

  return (
    <Form>
      <TicketRestartedWarning restartedAsTicketId={task.ticketRestartedAsTicketId} />
      <Row>
        <Col xl={colSize}>
          <WhiteBox
            header='Tasks.Task'
            buttons={canEdit &&
              <TaskActivationButton showButton={showButton} taskId={task.id} activeTaskId={activeTaskId}
                activating={activating} onActivate={activateTask} onDeactivate={deactivateTask}
                showText />}>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Common.Id' /> </InfoLabel>
              <InfoContent>{task.id}</InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.Status' /></InfoLabel>
              <InfoContent>
                <StatusEnum enumName={workflowTaskStatus.enumName} value={task.status} />
              </InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.Type' /></InfoLabel>
              <InfoContent>
                <Name data={task.type} />
                {!!scope && <Link to={'/' + scope}><Icon name='link-external' /></Link>}
              </InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.AssignedTo' /></InfoLabel>
              <InfoContent>
                {task.assignedTo}
                <ReassignTaskButton className={'btn btn-sm btn-primary'} task={task} />
              </InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.DueDate' /></InfoLabel>
              <InfoContent><DueDateWrapper dueDate={moment(task.dueDate)} /></InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Common.CreationTime' /></InfoLabel>
              <InfoContent>{formatDate(task.creationTime)}</InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.StartDate' /></InfoLabel>
              <InfoContent>{formatDateTime(task.startTime)}</InfoContent>
            </InfoRow>
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.FinishDate' /></InfoLabel>
              <InfoContent>{formatDateTime(task.finishTime)}</InfoContent>
            </InfoRow>
            {renderDescriptionBox()}
            <InfoRow>
              <InfoLabel size='sm'><Resx name='Tasks.Attachments' /></InfoLabel>
              <InfoContent>
                <FileAttachments
                  readonly={task.status === workflowTaskStatus.enum.Closed}
                  attachments={attachments}
                  onChange={onAttachmentChange}
                  onFilesAdded={handleUpdateAttachments}
                  downloadAction={context.api.taskService.download} />
              </InfoContent>
            </InfoRow>
          </WhiteBox>
        </Col>
        {middleColActive &&
          <Col xl={colSize}>
            <RelatedTask task={task} />
            {canViewCHR && !!(task.changeRequestId && task.data.resultType === 1) &&
              <ChangeRequestDetails
                canViewCHR={canViewCHR}
                changeRequestId={task.changeRequestId} loading={changeItemDeleting}
                deleteChangeItem={isActive ? deleteChangeItem : null}
                task={task} />
            }
            {!!(task.data.resultType) &&
              <TaskResultViewer task={task} isActive={isActive} />
            }
          </Col>
        }
        <Col xl={colSize}>
          {renderTicketDetail()}
        </Col>
      </Row>
    </Form>
  )
}

TaskDetailForm.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.number.isRequired,
    type: PropTypes.object.isRequired,
    assignedTo: PropTypes.string.isRequired,
    dueDate: PropTypes.string,
    creationTime: PropTypes.string.isRequired,
    startTime: PropTypes.string,
    finishTime: PropTypes.string,
    ticketId: PropTypes.number,
    ticketRestartedAsTicketId: PropTypes.number,
    relatedTaskId: PropTypes.number,
    description: PropTypes.string,
    latinDescription: PropTypes.string,
    attachments: PropTypes.array
  }).isRequired,
  onEditDescriptionButtonClick: PropTypes.func.isRequired
}

export default TaskDetailForm



// WEBPACK FOOTER //
// ./src/internal-app/pages/tasks/detailForm.js